import { useMutation, useQuery } from '@tanstack/react-query'
import { post } from '@/services/http'
import { endpoints } from '@/services/endpoints'
import { toInstant } from '@/features/anomalies/api'

/**
 * The consent step of connecting an MCP client to ForgeAI.
 *
 * The backend owns the whole OAuth exchange. It redirects the browser to
 * `/app/mcp-authorize?token=…`, and these two calls are all the page does with
 * that token: read what is being asked for, then send back yes or no.
 *
 * The token is a bearer secret for the length of the request. It travels in a
 * POST body — never a query string the transport might log — and nothing here
 * puts it in a query key, a log line or the screen.
 */

/** What is asking, where it will send the user, and until when. */
export async function previewRequest({ token, signal }) {
  const data = await post(endpoints.mcp.preview, { token }, { signal })
  return {
    clientName: data?.client_name ?? '',
    redirectHost: data?.redirect_host ?? '',
    scopeDescription: data?.scope_description ?? '',
    expiresAt: toInstant(data?.expires_at),
  }
}

/**
 * Allow or deny. Either answer comes back as a URL to hand the browser to —
 * the client's callback with a code, or with `error=access_denied`.
 */
export async function decideRequest({ token, approve }) {
  const data = await post(endpoints.mcp.decision, { token, approve })
  return { redirectUrl: data?.redirect_url ?? null }
}

/**
 * Not keyed on the token, so the secret does not sit in the query cache's index;
 * `gcTime: 0` drops the entry as soon as the page unmounts. A page holds exactly
 * one request, so there is nothing a token in the key would tell apart.
 *
 * Never retried and never refetched: a 400 here is an expired or forged token,
 * and asking again only repeats the answer.
 */
export function useMcpPreview(token) {
  return useQuery({
    queryKey: ['mcp', 'preview'],
    queryFn: ({ signal }) => previewRequest({ token, signal }),
    enabled: Boolean(token),
    retry: false,
    gcTime: 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })
}

/** Not retried. The decision is single-use on the server; a second send is a 400. */
export function useMcpDecision() {
  return useMutation({ mutationFn: decideRequest, retry: false })
}
